import { useEffect, useState } from 'react';
import type { AgentRow, Api, CallRow } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { duration, PartyLabel, resultOf, when } from './Calls';

type Go = (route: 'overview' | 'agents' | 'studio' | 'agent' | 'calls' | 'call', params?: Record<string, string>) => void;
/** `need`: the permission the call to action leads to (ADR-071); without it the item stays as information only. */
type Attention = { id: string; tone: 'warn' | 'bad' | 'info'; title: string; body: string; cta: string; act: () => void; need?: string };

const isToday = (iso: string): boolean => new Date(iso).toDateString() === new Date().toDateString();

/**
 * Overview — "what should I do next" for the business (layout from the legacy panel's work queue).
 * Built only from existing endpoints; each source fails soft so the page never blanks.
 */
export const OverviewPage = ({ api, t, go }: { api: Api; t: T; go: Go }) => {
  // ADR-071: buttons that lead into work the person may not do (create/publish/complete an agent, place a call) are not offered.
  const can = useCan();
  const [calls, setCalls] = useState<CallRow[] | null>(null);
  const [callbacks, setCallbacks] = useState<CallRow[] | null>(null);
  const [agents, setAgents] = useState<Array<AgentRow & { draftDiffers: boolean }> | null>(null);
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState<Array<{ agentId: string; name: string; score: number; missingHigh: number; missing: number }>>([]);

  const load = async () => {
    setLoading(true);
    const [c, cb, a] = await Promise.allSettled([api.calls('all'), api.calls('callbacks'), api.agents()]);
    void api.readinessSummary().then((r) => setReadiness(r.agents)).catch(() => undefined);
    setCalls(c.status === 'fulfilled' ? c.value.calls : null);
    setCallbacks(cb.status === 'fulfilled' ? cb.value.calls : null);
    if (a.status === 'fulfilled') {
      // Unpublished changes are per agent (a handful of agents per business).
      const details = await Promise.allSettled(a.value.items.slice(0, 20).map((x) => api.agent(x.id)));
      setAgents(a.value.items.map((x, i) => ({ ...x, draftDiffers: details[i]?.status === 'fulfilled' ? Boolean(details[i].value.published?.draftDiffers) : false })));
    } else setAgents(null);
    setLoading(false);
  };
  useEffect(() => void load(), [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const today = (calls ?? []).filter((c) => isToday(c.createdAt));
  const talked = today.filter((c) => c.status === 'ended' && !c.outcome?.answeredBy && (c.durationSeconds ?? 0) > 5);
  const machine = today.filter((c) => c.outcome?.answeredBy === 'machine');
  const errors = today.filter((c) => c.endedBy === 'error');
  const avg = talked.length ? Math.round(talked.reduce((s, c) => s + (c.durationSeconds ?? 0), 0) / talked.length) : null;
  const published = (agents ?? []).filter((a) => a.publishedPackageId);
  const changed = (agents ?? []).filter((a) => a.publishedPackageId && a.draftDiffers);
  const auto = (callbacks ?? []).filter((c) => c.callbackStatus === 'scheduled' && c.callbackAt).sort((a, b) => (a.callbackAt! < b.callbackAt! ? -1 : 1));
  const manual = (callbacks ?? []).filter((c) => !(c.callbackStatus === 'scheduled' || c.callbackStatus === 'dialing'));
  const dash = (v: number | null | undefined) => (v === null || v === undefined ? '—' : String(v));

  const kpis = [
    { id: 'today', value: calls ? String(today.length) : '—', tone: '', act: () => go('calls') },
    { id: 'talked', value: calls ? String(talked.length) : '—', tone: 'ok', act: () => go('calls') },
    { id: 'callbacks', value: dash(callbacks?.length), tone: callbacks?.length ? 'warn' : '', act: () => go('calls', { tab: 'callbacks' }) },
    { id: 'machine', value: calls ? String(machine.length) : '—', tone: '', act: () => go('calls') },
    { id: 'avg', value: avg === null ? '—' : duration(avg), tone: '', act: () => go('calls') },
    { id: 'agents', value: agents ? String(published.length) : '—', tone: published.length ? 'ok' : 'bad', act: () => go('agents') },
  ];

  const attention: Attention[] = [];
  if (agents && published.length === 0)
    attention.push({ id: 'noAgent', tone: 'bad', title: t('overview.att.noAgent'), body: t('overview.att.noAgent.body'), cta: t('overview.att.noAgent.cta'), act: () => go(agents.length ? 'agents' : 'studio'), ...(agents.length ? {} : { need: PERM.agents }) });
  if (manual.length > 0)
    attention.push({ id: 'manual', tone: 'warn', title: t('overview.att.manual', { n: manual.length }), body: t('overview.att.manual.body'), cta: t('overview.att.callbacks.cta'), act: () => go('calls', { tab: 'callbacks' }) });
  if (auto.length > 0)
    attention.push({ id: 'auto', tone: 'info', title: t('overview.att.auto', { n: auto.length }), body: t('overview.att.auto.body', { at: when(auto[0]!.callbackAt!), agent: auto[0]!.agentName }), cta: t('overview.att.callbacks.cta'), act: () => go('calls', { tab: 'callbacks' }) });
  for (const a of changed)
    attention.push({ id: `changed-${a.id}`, tone: 'warn', title: t('overview.att.changed', { name: a.name }), body: t('overview.att.changed.body'), cta: t('overview.att.changed.cta'), act: () => go('agent', { id: a.id }), need: PERM.agents });
  // Missing important info: the agent works, but keeps being reminded until answered or marked "does not apply".
  for (const r of readiness.filter((x) => x.missingHigh > 0))
    attention.push({ id: `ready-${r.agentId}`, tone: 'warn', title: t('overview.att.readiness', { name: r.name, n: r.missingHigh }), body: t('overview.att.readiness.body', { score: r.score }), cta: t('overview.att.readiness.cta'), act: () => go('agent', { id: r.agentId, tab: 'readiness' }), need: PERM.agents });
  if (errors.length > 0)
    attention.push({ id: 'errors', tone: 'bad', title: t('overview.att.errors', { n: errors.length }), body: t('overview.att.errors.body'), cta: t('overview.att.errors.cta'), act: () => go('calls') });

  const firstLive = published[0];

  return (
    <section className="overview">
      <div className="ops-strip">
        <span className="ops-badge">{t('overview.badge')}</span>
        <span>{t('overview.strip')}</span>
      </div>

      <div className="card queue">
        <div className="row-between">
          <div>
            <div className="kicker">{t('overview.queue.kicker')}</div>
            <h2>{t('overview.queue.title')}</h2>
          </div>
          <button type="button" className="secondary" disabled={loading} onClick={() => void load()}>{loading ? t('boot.loading') : t('overview.refresh')}</button>
        </div>
        <div className="kpis">
          {kpis.map((k) => (
            <button key={k.id} type="button" className={`kpi ${k.tone}`} onClick={k.act}>
              <span className="kpi-label">{t(`overview.kpi.${k.id}`)}</span>
              <span className="kpi-value">{k.value}</span>
            </button>
          ))}
        </div>
        {attention.length === 0 && !loading && <div className="attention info"><div><strong>{t('overview.att.none')}</strong><p className="hint">{t('overview.att.none.body')}</p></div></div>}
        {attention.map((a) => (
          <div key={a.id} className={`attention ${a.tone}`}>
            <div>
              <strong>{a.title}</strong>
              <p className="hint">{a.body}</p>
            </div>
            {can(a.need) && <button type="button" className="primary" onClick={a.act}>{a.cta}</button>}
          </div>
        ))}
      </div>

      <div className="command">
        <div>
          <div className="kicker">{t('overview.command.kicker')}</div>
          <h1>{t('overview.command.title')}</h1>
          <p className="muted">{t('overview.command.body')}</p>
          <div className="row">
            {firstLive ? (
              can(PERM.call) && <button type="button" className="primary" onClick={() => go('agent', { id: firstLive.id })}>☏ {t('overview.command.call', { name: firstLive.name })}</button>
            ) : (
              can(PERM.agents) && <button type="button" className="primary" onClick={() => go('studio')}>✦ {t('overview.command.create')}</button>
            )}
            <button type="button" className="secondary" onClick={() => go('calls')}>{t('overview.command.review')}</button>
            <button type="button" className="secondary" onClick={() => go('calls', { tab: 'callbacks' })}>{t('overview.command.callbacks')}</button>
          </div>
        </div>
        <div className="steps">
          {(['create', 'publish', 'call', 'follow'] as const).map((s, i) => (
            <div key={s} className="step">
              <span className="step-no">{i + 1}</span>
              <span><strong>{t(`overview.step.${s}`)}</strong><small>{t(`overview.step.${s}.body`)}</small></span>
            </div>
          ))}
        </div>
      </div>

      <div className="section-gap">
        <div className="row-between">
          <h2>{t('overview.recent')}</h2>
          <button type="button" className="link" onClick={() => go('calls')}>{t('overview.recent.all')} →</button>
        </div>
        {calls && calls.length === 0 && <div className="empty"><p>{t('calls.empty')}</p></div>}
        {calls && calls.length > 0 && (
          <div className="list">
            {calls.slice(0, 6).map((c) => {
              const r = resultOf(c, t);
              return (
                <div key={c.id} className="list-row clickable" onClick={() => go('calls', { call: c.id })}>
                  <div className="grow">
                    <div className="title"><PartyLabel t={t} row={c} /></div>
                    <div className="sub">{when(c.createdAt)} · {c.agentName} · {duration(c.durationSeconds)}</div>
                  </div>
                  <span className={`pill ${r.kind}`}>{r.text}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};
